import React from "react";
import {
    Badge, Box, Button, Checkbox, Chip, Divider, FormControlLabel, IconButton,
    List, ListItem, ListItemButton, ListItemText, Menu, Switch, Tooltip, Typography,
} from "@mui/material";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { APP_NAME } from "../../App";
import ApiNotificationCtrl from "../../api/ApiNotificationCtrl";
import { pushSupported, getPushState, enablePush, disablePush, showLocalNotification, notificationDelivered } from "../../utils/webpush";
import { markdownToText } from "../markdown/markdown";

// Types souscriptibles + rendu
const TYPES = [
    { key: "BUG_REPORT",     label: "Bugs",           icon: <BugReportRoundedIcon fontSize="small" /> },
    { key: "FEATURE_REQUEST",label: "Fonctionnalités",icon: <LightbulbRoundedIcon fontSize="small" /> },
    { key: "USER_CREATION",  label: "Créations de compte", icon: <PersonAddRoundedIcon fontSize="small" /> },
    { key: "CONTACT",        label: "Contacts",       icon: <EmailRoundedIcon fontSize="small" /> },
    { key: "PASSWORD_RESET", label: "Resets mot de passe", icon: <LockResetRoundedIcon fontSize="small" /> },
    { key: "INFRA_ALERT",    label: "Alertes infrastructure", icon: <MonitorHeartRoundedIcon fontSize="small" /> },
];
const TYPE_MAP = Object.fromEntries(TYPES.map(t => [t.key, t]));

// Statuts de suivi — libellés/couleurs alignés sur la page /admin/notifications
const STATUS_META = {
    TODO:        { label: "À traiter", color: "warning" },
    IN_PROGRESS: { label: "En cours",  color: "info" },
    TO_TEST:     { label: "À tester",  color: "secondary" },
    DONE:        { label: "Réalisé",   color: "success" },
};

const POLL_MS = 60000;

function fmtDate(ms) {
    if (!ms) return "";
    return new Date(ms).toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
}

function summaryOf(n) {
    if (n.type === "CONTACT") return n.subject || n.content || "(message)";
    if (n.type === "USER_CREATION") return n.username || n.email || "(compte)";
    if (n.type === "PASSWORD_RESET") return n.email || "(reset mot de passe)";
    return n.title || markdownToText(n.description) || "(sans description)";
}

/**
 * Cloche in-app, à deux volets :
 * <ul>
 *   <li><b>tout utilisateur connecté</b> — « mes signalements » : suivi des
 *       signalements qu'il a déposés, badgé à chaque changement de statut ;</li>
 *   <li><b>admins</b> — les signalements des autres, filtrés par types souscrits,
 *       avec le réglage des souscriptions.</li>
 * </ul>
 */
export default function NotificationBell() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();

    const isAdmin = user?.instances?.[APP_NAME]?.role === "ADMIN";
    // Clé de souscription : le login s'il existe, l'email sinon — la même que celle
    // envoyée comme `username` par le FAB de signalement, sinon le déclarant ne se
    // retrouve pas dans ses propres signalements.
    const admin = user?.login || user?.email || "";
    const myEmail = user?.email || "";

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [types, setTypes] = React.useState([]);
    const [lastSeenAt, setLastSeenAt] = React.useState(0);
    const [openThreshold, setOpenThreshold] = React.useState(0);
    const [notifs, setNotifs] = React.useState([]);
    const [mine, setMine] = React.useState([]);
    const [reporterLastSeenAt, setReporterLastSeenAt] = React.useState(0);
    const [reporterThreshold, setReporterThreshold] = React.useState(0);
    const [pushOn, setPushOn] = React.useState(false);
    const [pushBusy, setPushBusy] = React.useState(false);

    const open = Boolean(anchorEl);

    // Ids déjà vus par ce navigateur (null = premier chargement, on ne notifie pas
    // pour l'historique). Sert à déclencher une notification locale à l'arrivée
    // d'un signalement quand un onglet est ouvert — indépendamment du Web Push.
    const knownIdsRef = React.useRef(null);
    // Types souscrits lus dans le polling sans en faire une dépendance (sinon
    // l'intervalle serait recréé à chaque changement de case cochée).
    const typesRef = React.useRef([]);
    React.useEffect(() => { typesRef.current = types; }, [types]);

    const loadNotifs = React.useCallback(async () => {
        const { data } = await ApiNotificationCtrl.getNotifications();
        if (!Array.isArray(data)) return;
        setNotifs(data);

        if (knownIdsRef.current === null) {
            knownIdsRef.current = new Set(data.map(n => n.id));
            return;
        }
        const fresh = data.filter(n => !knownIdsRef.current.has(n.id));
        fresh.forEach(n => knownIdsRef.current.add(n.id));

        const toNotify = fresh.filter(n => typesRef.current.includes(n.type));
        if (toNotify.length === 1) {
            const n = toNotify[0];
            showLocalNotification(
                `${TYPE_MAP[n.type]?.label || "Signalement"} — nouveau`,
                summaryOf(n)
            );
        } else if (toNotify.length > 1) {
            showLocalNotification(
                `${toNotify.length} nouveaux signalements`,
                toNotify.map(summaryOf).join(" · ").slice(0, 120)
            );
        }
    }, []);

    // Statuts connus par signalement propre (null = premier chargement : on ne
    // notifie pas rétroactivement l'historique).
    const knownStatusRef = React.useRef(null);

    const loadMine = React.useCallback(async () => {
        if (!admin) return;
        const { data } = await ApiNotificationCtrl.getMyNotifications(admin, myEmail);
        if (!Array.isArray(data)) return;
        setMine(data);

        if (knownStatusRef.current === null) {
            knownStatusRef.current = new Map(data.map(n => [n.id, n.status]));
            return;
        }
        const changed = data.filter(n =>
            knownStatusRef.current.has(n.id) && knownStatusRef.current.get(n.id) !== n.status);
        data.forEach(n => knownStatusRef.current.set(n.id, n.status));

        if (changed.length === 1) {
            const n = changed[0];
            showLocalNotification(
                `${t("notifications.myReportChanged", "Votre signalement")} : ${STATUS_META[n.status]?.label || n.status}`,
                summaryOf(n)
            );
        } else if (changed.length > 1) {
            showLocalNotification(
                t("notifications.myReportsChanged", "Vos signalements ont été mis à jour"),
                changed.map(n => `${summaryOf(n)} → ${STATUS_META[n.status]?.label || n.status}`).join(" · ").slice(0, 120)
            );
        }
    }, [admin, myEmail, t]);

    const loadSubscription = React.useCallback(async () => {
        if (!admin) return;
        const { data } = await ApiNotificationCtrl.getSubscription(admin);
        if (data) {
            setTypes(Array.isArray(data.types) ? data.types : []);
            setLastSeenAt(data.lastSeenAt || 0);
            setReporterLastSeenAt(data.reporterLastSeenAt || 0);
        }
    }, [admin]);

    // Chargement initial + polling — le volet « mes signalements » vaut pour tout
    // utilisateur connecté, la liste globale seulement pour les admins.
    React.useEffect(() => {
        if (!admin) return;
        loadSubscription();
        loadMine();
        const id = setInterval(loadMine, POLL_MS);
        return () => clearInterval(id);
    }, [admin, loadSubscription, loadMine]);

    React.useEffect(() => {
        if (!isAdmin) return;
        loadNotifs();
        const id = setInterval(loadNotifs, POLL_MS);
        return () => clearInterval(id);
    }, [isAdmin, loadNotifs]);

    // État des notifications navigateur (Web Push) — ouvert à tout utilisateur
    // connecté : un déclarant doit pouvoir être alerté du suivi de son signalement.
    React.useEffect(() => {
        if (!admin || !pushSupported()) return;
        getPushState().then(s => setPushOn(s.subscribed)).catch(() => {});
    }, [admin]);

    async function togglePush(e) {
        const wanted = e.target.checked;
        setPushBusy(true);
        try {
            if (wanted) {
                await enablePush(admin);
                setPushOn(true);
                showToast?.({ msg: t("notifications.pushOn", "Notifications navigateur activées"), severity: "success" });
            } else {
                await disablePush(admin);
                setPushOn(false);
                showToast?.({ msg: t("notifications.pushOff", "Notifications navigateur désactivées"), severity: "info" });
            }
        } catch (err) {
            const msg = err?.message === "denied"
                ? t("notifications.pushDenied", "Permission refusée dans le navigateur")
                : t("notifications.pushError", "Impossible d'activer les notifications navigateur");
            showToast?.({ msg, severity: "error" });
            setPushOn(false);
        } finally {
            setPushBusy(false);
        }
    }

    /** Vérifie la chaîne complète : serveur → service worker → notification système. */
    async function testPush() {
        setPushBusy(true);
        const { data, error } = await ApiNotificationCtrl.pushTest(admin);
        setPushBusy(false);
        if (error) {
            showToast?.({ msg: t("notifications.pushTestError", "Échec de l'envoi du test"), severity: "error" });
            return;
        }
        if (data?.sent > 0) {
            // Le serveur a bien remis le push au service de push : reste à savoir si
            // le navigateur en a fait une notification système. Si l'objet existe
            // mais que rien n'apparaît à l'écran, le blocage est au niveau de l'OS —
            // le dire ici plutôt que d'annoncer un succès trompeur.
            const delivered = await notificationDelivered("TEST");
            showToast?.({
                msg: delivered
                    ? t("notifications.pushTestDelivered",
                        "Notification remise au navigateur. Si rien ne s'affiche à l'écran, autorisez le navigateur dans les réglages de notifications du système.")
                    : t("notifications.pushTestSent", "Notification de test envoyée"),
                severity: delivered ? "info" : "success",
            });
        } else {
            // Le serveur n'a trouvé aucun appareil : on le dit plutôt que de laisser
            // croire à un test réussi qui n'affiche rien.
            showToast?.({
                msg: t("notifications.pushTestNoDevice", "Aucun appareil abonné — activez d'abord les notifications navigateur"),
                severity: "warning",
            });
        }
    }

    // Types souscrits ET non encore résolus (les "Réalisé" disparaissent de la cloche)
    const subscribed = React.useMemo(
        () => notifs.filter(n => types.includes(n.type) && n.status !== "DONE"),
        [notifs, types]
    );
    const unseenCount = React.useMemo(
        () => subscribed.filter(n => (n.createdAt || 0) > (lastSeenAt || 0)).length,
        [subscribed, lastSeenAt]
    );

    // Mes signalements dont le statut a bougé depuis ma dernière consultation
    const myUnseenCount = React.useMemo(
        () => mine.filter(n => (n.statusChangedAt || 0) > (reporterLastSeenAt || 0)).length,
        [mine, reporterLastSeenAt]
    );

    const badgeCount = (isAdmin ? unseenCount : 0) + myUnseenCount;

    async function handleOpen(e) {
        setOpenThreshold(lastSeenAt || 0);            // fige les seuils "nouveau"
        setReporterThreshold(reporterLastSeenAt || 0);
        setAnchorEl(e.currentTarget);
        if (isAdmin) await loadNotifs();
        await loadMine();
        if (admin) {
            if (isAdmin) {
                ApiNotificationCtrl.markSeen(admin);   // clôt le badge côté serveur
                setLastSeenAt(Date.now());             // optimiste : badge → 0
            }
            ApiNotificationCtrl.markMineSeen(admin);
            setReporterLastSeenAt(Date.now());
        }
    }

    async function toggleType(key) {
        const prev = types;
        const next = prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key];
        setTypes(next);                       // optimiste
        if (!admin) return;
        const { error } = await ApiNotificationCtrl.saveSubscription(admin, next);
        if (error) {
            setTypes(prev);                   // rollback si le serveur refuse
            showToast?.({ msg: t("notifications.saveError", "Échec de la mise à jour de la souscription"), severity: "error" });
        }
    }

    function goTo() {
        setAnchorEl(null);
        navigate("/admin/notifications");
    }

    // Un visiteur non connecté n'a ni signalement ni souscription à consulter.
    if (!admin) return null;

    return (
        <>
            <Tooltip title={t("notifications.bell", "Notifications")}>
                <IconButton color="inherit" onClick={handleOpen} size="small" aria-label={t("notifications.bell", "Notifications")}>
                    <Badge badgeContent={badgeCount} color="error" max={99}>
                        {badgeCount > 0 ? <NotificationsRoundedIcon /> : <NotificationsNoneRoundedIcon />}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { sx: { width: 380, maxWidth: "90vw", borderRadius: 2 } } }}
            >
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {t("notifications.bell", "Notifications")}
                    </Typography>
                    {isAdmin && (
                        <>
                            <Typography variant="caption" color="text.secondary">
                                {t("notifications.subscribeHint", "Je souscris à :")}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                {TYPES.map(ty => (
                                    <FormControlLabel
                                        key={ty.key}
                                        sx={{ m: 0, mr: 1 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={types.includes(ty.key)}
                                                onChange={() => toggleType(ty.key)}
                                            />
                                        }
                                        label={<Typography variant="caption">{ty.label}</Typography>}
                                    />
                                ))}
                            </Box>
                        </>
                    )}
                    {pushSupported() && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                            <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                    <Switch
                                        size="small"
                                        checked={pushOn}
                                        disabled={pushBusy}
                                        onChange={togglePush}
                                    />
                                }
                                label={
                                    <Typography variant="caption">
                                        {t("notifications.pushToggle", "Notifications navigateur (arrière-plan)")}
                                    </Typography>
                                }
                            />
                            {pushOn && (
                                <Button size="small" variant="text" disabled={pushBusy} onClick={testPush}>
                                    {t("notifications.pushTest", "Tester")}
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
                <Divider />

                {/* ── Mes signalements : suivi du statut, pour tout utilisateur ── */}
                {mine.length > 0 && (
                    <>
                        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                {t("notifications.mySection", "Mes signalements")}
                            </Typography>
                        </Box>
                        <List dense sx={{ maxHeight: 260, overflowY: "auto", py: 0 }}>
                            {mine.slice(0, 20).map(n => {
                                const meta = TYPE_MAP[n.type] || {};
                                const st = STATUS_META[n.status] || { label: n.status, color: "default" };
                                const isNew = (n.statusChangedAt || 0) > (reporterThreshold || 0);
                                return (
                                    <ListItem key={n.id} sx={{ alignItems: "flex-start", gap: 1 }}>
                                        <Box sx={{ mt: 0.5, color: isNew ? "primary.main" : "text.secondary" }}>{meta.icon}</Box>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: isNew ? 700 : 400, flex: 1 }} noWrap>
                                                        {summaryOf(n)}
                                                    </Typography>
                                                    <Chip size="small" color={st.color} label={st.label}
                                                          sx={{ height: 18, fontSize: 10 }} />
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    <Typography variant="caption" color="text.secondary" component="span">
                                                        {n.statusChangedAt
                                                            ? `${n.previousStatus
                                                                ? `${STATUS_META[n.previousStatus]?.label || n.previousStatus} → ${st.label}`
                                                                : st.label} · ${fmtDate(n.statusChangedAt)}`
                                                            : `${meta.label} · ${fmtDate(n.createdAt)}`}
                                                    </Typography>
                                                    {n.resolutionNote && (
                                                        <Typography variant="caption" color="text.secondary"
                                                                    component="span" sx={{ display: "block", fontStyle: "italic" }}>
                                                            {n.resolutionNote}
                                                        </Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                        <Divider />
                    </>
                )}

                {isAdmin && (
                <List dense sx={{ maxHeight: 340, overflowY: "auto", py: 0 }}>
                    {subscribed.length === 0 && (
                        <Box sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary">
                                {t("notifications.empty", "Aucune notification")}
                            </Typography>
                        </Box>
                    )}
                    {subscribed.slice(0, 20).map(n => {
                        const meta = TYPE_MAP[n.type] || {};
                        const isNew = (n.createdAt || 0) > (openThreshold || 0);
                        return (
                            <ListItemButton key={n.id} onClick={goTo} sx={{ alignItems: "flex-start", gap: 1 }}>
                                <Box sx={{ mt: 0.5, color: isNew ? "primary.main" : "text.secondary" }}>{meta.icon}</Box>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: isNew ? 700 : 400, flex: 1 }} noWrap>
                                                {summaryOf(n)}
                                            </Typography>
                                            {isNew && <Chip size="small" color="primary" label={t("notifications.new", "nouveau")} sx={{ height: 18, fontSize: 10 }} />}
                                        </Box>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary">
                                            {meta.label} · {fmtDate(n.createdAt)}
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
                )}

                {!isAdmin && mine.length === 0 && (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                            {t("notifications.emptyMine", "Vous n'avez déposé aucun signalement")}
                        </Typography>
                    </Box>
                )}

                {isAdmin && (
                    <>
                        <Divider />
                        <ListItemButton onClick={goTo} sx={{ justifyContent: "center", py: 1 }}>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                {t("notifications.seeAll", "Voir tous les signalements")}
                            </Typography>
                        </ListItemButton>
                    </>
                )}
            </Menu>
        </>
    );
}
